import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Token refresh interval (refresh 1 hour before expiry, assuming 72-hour tokens)
const TOKEN_REFRESH_INTERVAL = 71 * 60 * 60 * 1000; // 71 hours in ms
const TOKEN_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Helper to check if token is near expiry
const isTokenExpiringSoon = (token) => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to ms
    const now = Date.now();
    const oneHourFromNow = now + (60 * 60 * 1000);
    return exp < oneHourFromNow;
  } catch {
    return true;
  }
};

// Helper to check if token is valid (not expired)
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000;
    return Date.now() < exp;
  } catch {
    return false;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("prana_token"));
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const refreshTimeoutRef = useRef(null);
  const checkIntervalRef = useRef(null);

  // Clear session completely
  const clearSession = useCallback(() => {
    localStorage.removeItem("prana_token");
    localStorage.removeItem("prana_token_timestamp");
    setToken(null);
    setUser(null);
    setSessionError(null);
  }, []);

  // Validate and restore session
  const validateSession = useCallback(async (currentToken) => {
    if (!currentToken || !isTokenValid(currentToken)) {
      return false;
    }
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        timeout: 10000 // 10 second timeout
      });
      setUser(response.data);
      setSessionError(null);
      return true;
    } catch (error) {
      console.error("Session validation error:", error?.response?.status || error.message);
      // Only clear session on 401/403, not on network errors
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        clearSession();
      } else {
        setSessionError("Network error - session may be unstable");
      }
      return false;
    }
  }, [clearSession]);

  // Initialize auth on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const initAuth = async () => {
      const storedToken = localStorage.getItem("prana_token");
      
      if (storedToken) {
        // Check if stored token is still valid
        if (!isTokenValid(storedToken)) {

          clearSession();
          setLoading(false);
          return;
        }
        
        setToken(storedToken);
        await validateSession(storedToken);
      }
      setLoading(false);
    };
    
    initAuth();
    
    // Cleanup on unmount — snapshot ref values to avoid stale-ref-in-cleanup warning.
    const refreshTimeout = refreshTimeoutRef.current;
    const checkInterval = checkIntervalRef.current;
    return () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      if (checkInterval) clearInterval(checkInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateSession, clearSession]);

  // Periodic session health check
  useEffect(() => {
    if (!token) return;
    
    // Check session health periodically
    checkIntervalRef.current = setInterval(() => {
      if (token && !isTokenValid(token)) {

        clearSession();
      }
    }, TOKEN_CHECK_INTERVAL);
    
    return () => {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [token, clearSession]);

  // Handle visibility change (tab focus/blur)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && token) {
        // Re-validate session when tab becomes visible
        await validateSession(token);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [token, validateSession]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem("prana_token", access_token);
      localStorage.setItem("prana_token_timestamp", Date.now().toString());
      setToken(access_token);
      setUser(userData);
      setSessionError(null);
      return userData;
    } catch (error) {
      setSessionError(error?.response?.data?.detail || "Login failed");
      throw error;
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { email, password, name });
      const { access_token, user: userData } = response.data;
      localStorage.setItem("prana_token", access_token);
      localStorage.setItem("prana_token_timestamp", Date.now().toString());
      setToken(access_token);
      setUser(userData);
      setSessionError(null);
      return userData;
    } catch (error) {
      setSessionError(error?.response?.data?.detail || "Registration failed");
      throw error;
    }
  };

  // Mobile OTP flow: backend returns the JWT directly after verify-otp succeeds.
  // This helper plugs it into the same session-state path that login/register use.
  const loginWithToken = useCallback((access_token, userData) => {
    localStorage.setItem("prana_token", access_token);
    localStorage.setItem("prana_token_timestamp", Date.now().toString());
    setToken(access_token);
    setUser(userData);
    setSessionError(null);
    return userData;
  }, []);

  const logout = () => {
    clearSession();
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  // Force re-authentication (useful for session recovery)
  const forceReauth = useCallback(async () => {
    if (token) {
      return await validateSession(token);
    }
    return false;
  }, [token, validateSession]);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      register,
      loginWithToken,
      logout,
      updateUser,
      getAuthHeaders,
      isAuthenticated: !!user && !!token && isTokenValid(token),
      sessionError,
      forceReauth,
      clearSession
    }}>
      {children}
    </AuthContext.Provider>
  );
};
